// src/components/FacilitySetupWizard.jsx
// UPDATED: Creates facility in facilities subcollection instead of club root
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { doc, getDoc, onSnapshot, setDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { auth, db } from '../utils/firebase';
import GoogleMapsSatelliteUpload from './satellite/GoogleMapsSatelliteUpload';

// Helper function to get pitch display name
const getPitchDisplayName = (pitchNumber) => {
  if (!pitchNumber) return 'Pitch';
  return `Pitch ${pitchNumber}`;
};

// Canvas component for rendering facility map
function PitchMapCanvas({ satelliteImage, pitches, imageWidth, imageHeight }) {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  const calculateCanvasSize = (imgWidth, imgHeight) => {
    if (!imgWidth || !imgHeight || imgWidth <= 0 || imgHeight <= 0) {
      return { width: 1000, height: 700 };
    }
    const maxWidth = window.innerWidth < 768 ? window.innerWidth - 40 : 1000;
    const maxHeight = window.innerWidth < 768 ? 400 : 700;
    const aspectRatio = imgWidth / imgHeight;
    
    let width = maxWidth;
    let height = width / aspectRatio;
    
    if (height > maxHeight) {
      height = maxHeight;
      width = height * aspectRatio;
    }
    
    if (width < 400) width = 400;
    if (height < 300) height = 300;
    
    return { width: Math.floor(width), height: Math.floor(height) };
  };

  const handleImageLoad = () => {
    if (imageRef.current) {
      const imgWidth = imageWidth || imageRef.current.naturalWidth;
      const imgHeight = imageHeight || imageRef.current.naturalHeight;
      const size = calculateCanvasSize(imgWidth, imgHeight);
      setCanvasSize(size);
      setImageLoaded(true);
    }
  };

  const drawPitchBoundary = (ctx, pitch, scaleX, scaleY, index) => {
    try {
      const centerX = pitch.centerX * scaleX;
      const centerY = pitch.centerY * scaleY;
      
      // If pitch has corners (new format), draw as polygon - MATCHES ClubPitchMap
      if (pitch.corners && pitch.corners.length === 4) {
        ctx.fillStyle = 'rgba(59, 130, 246, 0.35)';
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 2;
        
        ctx.beginPath();
        ctx.moveTo(pitch.corners[0].x * scaleX, pitch.corners[0].y * scaleY);
        for (let i = 1; i < 4; i++) {
          ctx.lineTo(pitch.corners[i].x * scaleX, pitch.corners[i].y * scaleY);
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      } else {
        // Legacy format: draw as rotated rectangle
        const width = pitch.width * scaleX;
        const height = pitch.height * scaleY;
        const rotation = pitch.rotation || 0;
        
        ctx.save();
        ctx.translate(centerX, centerY);
        ctx.rotate(rotation);
        
        ctx.fillStyle = 'rgba(59, 130, 246, 0.35)';
        ctx.fillRect(-width / 2, -height / 2, width, height);
        
        ctx.strokeStyle = '#3b82f6';
        ctx.lineWidth = 2;
        ctx.strokeRect(-width / 2, -height / 2, width, height);
        
        ctx.restore();
      }
      
      // Draw label at center
      let displayName;
      if (pitch.name) {
        displayName = pitch.name;
      } else if (pitch.pitchNumber) {
        displayName = getPitchDisplayName(pitch.pitchNumber);
      } else if (pitch.id) {
        const pitchNum = pitch.id.replace('pitch-', '').replace('pitch', '');
        displayName = getPitchDisplayName(pitchNum);
      } else {
        displayName = getPitchDisplayName(index + 1);
      }
      
      ctx.font = 'bold 20px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 4;
      ctx.strokeText(displayName, centerX, centerY);
      
      ctx.fillStyle = '#ffffff';
      ctx.fillText(displayName, centerX, centerY);
      
    } catch (error) {
      console.error('Error drawing pitch boundary:', error);
    }
  };

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    
    if (!canvas || !image || !imageLoaded || !canvasSize.width || !canvasSize.height) {
      return;
    }

    try {
      const ctx = canvas.getContext('2d');
      canvas.width = canvasSize.width;
      canvas.height = canvasSize.height;
      
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

      const scaleX = canvas.width / (imageWidth || image.naturalWidth);
      const scaleY = canvas.height / (imageHeight || image.naturalHeight);

      if (pitches && pitches.length > 0) {
        pitches.forEach((pitch, index) => {
          drawPitchBoundary(ctx, pitch, scaleX, scaleY, index);
        });
      }
      
      canvas.style.display = 'block';
      canvas.style.visibility = 'visible';
    } catch (error) {
      console.error('Error drawing canvas:', error);
    }
  }, [imageLoaded, pitches, canvasSize, imageWidth, imageHeight]);

  useEffect(() => {
    if (imageLoaded && canvasRef.current && canvasSize.width > 0 && canvasSize.height > 0) {
      setTimeout(() => {
        drawCanvas();
      }, 100);
    }
  }, [imageLoaded, drawCanvas, canvasSize]);

  if (!satelliteImage) {
    return (
      <div style={{ 
        height: '600px',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        color: '#9ca3af',
        backgroundColor: '#f9fafb'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>📷</div>
          <div>No Satellite Image</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      height: '600px',
      width: '100%', 
      overflow: 'auto',
      backgroundColor: '#f3f4f6',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{ position: 'relative' }}>
        <img
          ref={imageRef}
          src={satelliteImage}
          alt="Satellite view"
          onLoad={handleImageLoad}
          style={{ display: 'none' }}
          crossOrigin="anonymous"
        />
        <canvas
          ref={canvasRef}
          style={{
            display: imageLoaded ? 'block' : 'none',
            maxWidth: '100%',
            cursor: 'default'
          }}
        />
      </div>
    </div>
  );
}

function FacilitySetupWizard({ onComplete }) {
  const [clubId, setClubId] = useState(null);
  const [clubData, setClubData] = useState(null);
  const [facilityId, setFacilityId] = useState(null);
  const [facilityData, setFacilityData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasImage, setHasImage] = useState(false);
  const [hasPitches, setHasPitches] = useState(false);
  
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  useEffect(() => {
    console.log('🚀 FacilitySetupWizard MULTI-FACILITY VERSION loaded');
    
    const loadClubAndFacilityData = async () => {
      if (!auth.currentUser) return;

      try {
        const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.clubId) {
            setClubId(userData.clubId);
            
            // Get club data
            const clubDoc = await getDoc(doc(db, 'clubs', userData.clubId));
            if (clubDoc.exists()) {
              const club = clubDoc.data();
              setClubData(club);
              
              // Check for existing facilities
              const facilitiesRef = collection(db, 'clubs', userData.clubId, 'facilities');
              const facilitiesSnap = await getDocs(facilitiesRef);
              
              if (!facilitiesSnap.empty) {
                // Use primary facility or first facility
                const primaryFacilityId = club.primaryFacilityId;
                let targetFacility = null;
                
                facilitiesSnap.forEach((doc) => {
                  if (doc.id === primaryFacilityId || !targetFacility) {
                    targetFacility = { id: doc.id, ...doc.data() };
                  }
                });
                
                if (targetFacility) {
                  console.log('📦 Found existing facility:', targetFacility.id);
                  setFacilityId(targetFacility.id);
                  setFacilityData(targetFacility);
                  
                  // Check facility for image and pitches
                  const imageExists = !!(targetFacility.satelliteImage || targetFacility.satelliteImageUrl);
                  const pitchesExist = targetFacility.pitches && targetFacility.pitches.length > 0;
                  
                  console.log('🖼️ Facility image exists:', imageExists);
                  console.log('⚽ Facility pitches exist:', pitchesExist);
                  
                  setHasImage(imageExists);
                  setHasPitches(pitchesExist);
                  
                  // Set up real-time listener on facility
                  setupFacilityListener(userData.clubId, targetFacility.id);
                }
              } else {
                // No facilities exist yet - check legacy club-level data
                console.log('📦 No facilities found, checking legacy club data');
                
                const legacyImageExists = !!(club.satelliteImage || club.satelliteConfig?.satelliteImageUrl);
                const legacyPitchesExist = club.pitches && club.pitches.length > 0;
                
                if (legacyImageExists || legacyPitchesExist) {
                  console.log('📦 Found legacy data, will migrate on next save');
                  // Show legacy data but will create facility on save
                  setHasImage(legacyImageExists);
                  setHasPitches(legacyPitchesExist);
                }
              }
            }
            
            setIsLoading(false);
          }
        }
      } catch (error) {
        console.error('❌ Load error:', error);
        setIsLoading(false);
      }
    };
    
    const setupFacilityListener = (clubId, facilityId) => {
      const facilityRef = doc(db, 'clubs', clubId, 'facilities', facilityId);
      
      const unsubscribe = onSnapshot(facilityRef, (facilityDoc) => {
        if (facilityDoc.exists()) {
          const data = { id: facilityDoc.id, ...facilityDoc.data() };
          console.log('📦 Facility updated:', data);
          
          setFacilityData(data);
          
          const imageExists = !!(data.satelliteImage || data.satelliteImageUrl);
          const pitchesExist = data.pitches && data.pitches.length > 0;
          
          console.log('✅ Updated: Image=' + imageExists + ', Pitches=' + pitchesExist);
          setHasImage(imageExists);
          setHasPitches(pitchesExist);
        }
      }, (error) => {
        console.error('❌ Facility listener error:', error);
      });
      
      return unsubscribe;
    };

    loadClubAndFacilityData();
  }, []);

  // Handler for when satellite capture is complete
  // This is called by GoogleMapsSatelliteUpload when pitches are saved
  const handleCaptureComplete = async (captureData) => {
    console.log('🎯 Capture complete, creating/updating facility:', captureData);
    
    if (!clubId) {
      console.error('No clubId available');
      return;
    }
    
    try {
      const now = new Date().toISOString();
      
      // Use default dimensions if not provided
      const imgWidth = captureData.imageWidth || 2048;
      const imgHeight = captureData.imageHeight || 1536;
      
      if (facilityId) {
        // Update existing facility
        const facilityRef = doc(db, 'clubs', clubId, 'facilities', facilityId);
        await updateDoc(facilityRef, {
          satelliteImage: captureData.satelliteImage || captureData.imageUrl,
          satelliteImageUrl: captureData.satelliteImage || captureData.imageUrl,
          satelliteConfig: captureData.satelliteConfig || null,
          imageWidth: imgWidth,
          imageHeight: imgHeight,
          pitches: captureData.pitches || [],
          pitchCount: captureData.pitches?.length || 0,
          isConfigured: true,
          facilityConfigured: true,
          updatedAt: now
        });
        
        console.log('✅ Updated existing facility:', facilityId);
      } else {
        // Create new primary facility
        const newFacilityId = 'facility-1';
        const facilityRef = doc(db, 'clubs', clubId, 'facilities', newFacilityId);
        
        const facilityData = {
          id: newFacilityId,
          name: clubData?.name ? `${clubData.name} Main Ground` : 'Main Ground',
          address: captureData.address || '',
          isPrimary: true,
          isConfigured: true,
          facilityConfigured: true,
          isShared: false,
          sharedWith: null,
          dayAccessRules: null,
          configurationMethod: captureData.configurationMethod || 'manual_draw',
          satelliteConfig: captureData.satelliteConfig || null,
          satelliteImage: captureData.satelliteImage || captureData.imageUrl,
          satelliteImageUrl: captureData.satelliteImage || captureData.imageUrl,
          satelliteImageCapturedAt: now,
          imageWidth: imgWidth,
          imageHeight: imgHeight,
          pitchCount: captureData.pitches?.length || 0,
          pitches: captureData.pitches || [],
          createdAt: now,
          createdBy: auth.currentUser?.uid || 'system',
          updatedAt: now
        };
        
        await setDoc(facilityRef, facilityData);
        
        // Update club document with facility count and primary facility
        const clubRef = doc(db, 'clubs', clubId);
        await updateDoc(clubRef, {
          facilityCount: 1,
          primaryFacilityId: newFacilityId,
          pitchCount: captureData.pitches?.length || 0,
          facilityConfigured: true,
          updatedAt: now
        });
        
        setFacilityId(newFacilityId);
        setFacilityData(facilityData);
        
        console.log('✅ Created new facility:', newFacilityId);
      }
      
      setHasImage(true);
      setHasPitches(captureData.pitches && captureData.pitches.length > 0);
      
    } catch (error) {
      console.error('❌ Error saving facility:', error);
    }
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
        <div style={{ fontSize: '40px', marginBottom: '16px' }}>⚽</div>
        Loading facility setup...
      </div>
    );
  }

  const isComplete = hasImage && hasPitches;
  
  // Get display data from facility or legacy club data
  const displayData = facilityData || clubData;
  const satelliteImage = displayData?.satelliteImage || displayData?.satelliteConfig?.satelliteImageUrl;
  const pitches = displayData?.pitches || [];
  const imageWidth = displayData?.imageWidth;
  const imageHeight = displayData?.imageHeight;

  return (
    <div style={{ width: '100%' }}>
      {/* Show upload tool OR completed map */}
      {!isComplete ? (
        <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden', marginBottom: '24px' }}>
          <GoogleMapsSatelliteUpload
            clubId={clubId}
            facilityId={facilityId}
            satelliteConfig={facilityData?.satelliteConfig || clubData?.satelliteConfig}
            onCaptureComplete={handleCaptureComplete}
            googleMapsApiKey={googleMapsApiKey}
          />
        </div>
      ) : (
        <>
          {/* Success message */}
          <div style={{
            backgroundColor: '#f0fdf4',
            border: '2px solid #10b981',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
            <h3 style={{
              margin: '0 0 8px 0',
              color: '#15803d',
              fontSize: '20px',
              fontWeight: '700'
            }}>
              Facility Setup Complete!
            </h3>
            <p style={{
              margin: 0,
              color: '#6b7280',
              fontSize: '14px'
            }}>
              Your satellite image and pitch boundaries have been saved
              {facilityData?.name && ` for ${facilityData.name}`}
            </p>
          </div>

          {/* Show the pitch map */}
          <div style={{
            backgroundColor: 'white',
            border: '2px solid #10b981',
            borderRadius: '12px',
            overflow: 'hidden',
            marginBottom: '24px'
          }}>
            <div style={{
              padding: '16px 24px',
              backgroundColor: '#f0fdf4',
              borderBottom: '1px solid #bbf7d0'
            }}>
              <h4 style={{
                margin: 0,
                color: '#15803d',
                fontSize: '16px',
                fontWeight: '600'
              }}>
                📍 {facilityData?.name || 'Your Facility Map'}
              </h4>
            </div>
            <PitchMapCanvas 
              satelliteImage={satelliteImage}
              pitches={pitches}
              imageWidth={imageWidth}
              imageHeight={imageHeight}
            />
          </div>
        </>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '24px', borderTop: '1px solid #e5e7eb' }}>
        <div style={{ fontSize: '14px', color: isComplete ? '#10b981' : '#6b7280', fontWeight: isComplete ? '600' : '400' }}>
          {isComplete ? '✓ Facility setup complete - ready to continue' : 'Complete both steps above to continue'}
        </div>
        
        <div style={{ display: 'flex', gap: '12px' }}>
          {!isComplete && (
            <button onClick={onComplete} style={{
              padding: '12px 24px', backgroundColor: 'transparent', color: '#6b7280',
              border: '2px solid #d1d5db', borderRadius: '8px', cursor: 'pointer',
              fontSize: '14px', fontWeight: '600', transition: 'all 0.2s'
            }}>
              Skip for Now
            </button>
          )}
          
          <button onClick={onComplete} disabled={!isComplete} style={{
            padding: '14px 32px',
            backgroundColor: isComplete ? '#10b981' : '#9ca3af',
            color: 'white', border: 'none', borderRadius: '8px',
            cursor: isComplete ? 'pointer' : 'not-allowed',
            fontSize: '16px', fontWeight: '600', transition: 'all 0.2s',
            opacity: isComplete ? 1 : 0.6
          }}>
            Continue to Teams →
          </button>
        </div>
      </div>

      {!isComplete && (
        <div style={{ textAlign: 'center', marginTop: '16px', fontSize: '13px', color: '#9ca3af', fontStyle: 'italic' }}>
          Don't worry - you can always update your facility layout later from Settings
        </div>
      )}
    </div>
  );
}

export default FacilitySetupWizard;
